import type { ReactNode } from 'react';
import { Box, Stack } from '@mui/material';
import { PageHeader } from './PageHeader';

/**
 * Thin composition layer for administration pages.
 *
 * Not a replacement for AppShell — the shell owns chrome, breadcrumbs and the
 * main landmark. This owns only the inside of a page: heading, actions, an
 * optional filter region, and content.
 *
 * `maxWidth` exists because forms and tables want different measures. A form at
 * full width forces the eye across the screen for a two-word field; a table
 * constrained to 640px wastes the screen. Callers say which they are.
 */
export function AdminPageLayout({
  title,
  description,
  actions,
  secondaryActions,
  filters,
  maxWidth = 'full',
  children,
}: {
  title: string;
  description?: string;
  /** Primary action, rendered rightmost. */
  actions?: ReactNode;
  /** Lower-emphasis actions, rendered before the primary one. */
  secondaryActions?: ReactNode;
  /** Filter region, between header and content. */
  filters?: ReactNode;
  /** 'form' constrains to a comfortable reading measure; 'full' fills. */
  maxWidth?: 'form' | 'full';
  children: ReactNode;
}) {
  return (
    <Box sx={{ maxWidth: maxWidth === 'form' ? 640 : '100%' }}>
      {/* PageHeader renders <h1>; pages must not add another. */}
      <PageHeader
        title={title}
        subtitle={description}
        actions={
          actions || secondaryActions ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {secondaryActions}
              {actions}
            </Stack>
          ) : undefined
        }
      />
      {filters && <Box sx={{ mb: 2 }}>{filters}</Box>}
      <Box>{children}</Box>
    </Box>
  );
}
